import { getDeleteConnection } from '~/app/utils/getDeleteConnection';
import { type getActionsFn } from '~/app/utils/getJiraOnPremActions';
import { SupportedIntegrationProductSlugs } from '~/common/constants';
import {
  createIntegrationConnection,
  disconnectIntegrationConnection
} from '~/common/utils/integrations-client/jira/connections';
import { initiateConnection } from '~/common/utils/integrations-client/jira/on-prem/connection';

export const getJiraDirectActions: getActionsFn = ({
  displayName,
  integrationProductInfo,
  connection,
  authUser,
  enterprise,
  analytics,
  setIsProcessing,
  setError,
  connectionsUpdated,
  setContents,
  t
}) => {
  const connect = async (): Promise<void> => {
    try {
      setIsProcessing(true);

      const connectionToUse = connection
        ? connection
        : await createIntegrationConnection({
            enterpriseId: enterprise.id,
            token: authUser.token,
            body: {
              product_slug:
                SupportedIntegrationProductSlugs.jiraDirectCloudIntegration,
              status: 'disconnected'
            }
          });

      const initConnection = await initiateConnection({
        enterpriseId: enterprise.id,
        token: authUser.token,
        connectionId: connectionToUse.id
      });
      window.open(
        initConnection.redirect_url,
        '_blank',
        'popup,width=650,height=650'
      );
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);

      setError(
        t(
          'There was an error connecting to {{ displayName }}. Please try again.',
          {
            displayName
          }
        )
      );

      await analytics.integrationConnectFail({
        integration:
          integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
      });
    } finally {
      setIsProcessing(false);

      connectionsUpdated();
    }
  };

  const disconnect = async (): Promise<void> => {
    try {
      if (connection) {
        setIsProcessing(true);

        // Update server side connection
        await disconnectIntegrationConnection({
          enterpriseId: enterprise.id,
          token: authUser.token,
          connectionId: connection.id
        });

        setContents(
          t('Successfully disconnected from {{integrationType}}', {
            integrationType: displayName
          }),
          'confirmation'
        );

        await analytics.integrationDisconnect({
          integration:
            integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
        });

        connectionsUpdated();
      }
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);

      setError(
        t(
          'There was an error disconnecting from {{ displayName }}. Please try again.',
          {
            displayName
          }
        )
      );

      await analytics.integrationDisconnectFail({
        integration:
          integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteConnection = getDeleteConnection({
    displayName,
    connection,
    authUser,
    enterprise,
    setIsProcessing,
    setError,
    connectionsUpdated,
    setContents,
    t
  }).deleteConnection;

  return {
    connect,
    disconnect,
    deleteConnection
  };
};
